import i18n from "i18next"

import type { ManagementListParams } from "./ManagementTabFilter/types"

export interface ITabNav {
  /** Unique identifier for the tab */
  id: string
  /** Display content of the tab */
  content: string
  /** Optional translation key for i18n */
  tran_key?: string
  /** Filters associated with this tab */
  filters: Partial<ManagementListParams>
  /** Whether this is a custom view created by user */
  isCustomized: boolean
}

class Config {
  private _tabs: ITabNav[] = []
  private _defaultTabs: ITabNav[] = []

  get tabs() {
    return this._tabs
  }

  get defaultTabs() {
    return this._defaultTabs
  }

  /**
   * Initialize default tabs from TAB_DEFS
   */
  setDefaultTabs(tabs: ITabNav[]) {
    this._defaultTabs = tabs
    this.translateTabs(this._defaultTabs)
  }

  /**
   * Set the full tab list (default + custom)
   */
  setTabNavList(list: ITabNav[]) {
    this._tabs = list
    this.translateTabs(this._tabs)
  }

  /**
   * Translate tabs using i18n for default tabs
   */
  private translateTabs(tabs: ITabNav[]) {
    tabs.forEach((item) => {
      if (item.tran_key) {
        item.content = i18n.t(item.tran_key as any, {
          defaultValue: item.content,
          ns: "common",
        })
      }
    })
  }

  /**
   * Check if a tab name already exists
   */
  checkTabNameExists(tabName: string, excludeId?: string): boolean {
    return this._tabs.some(
      ({ content, id }) => content === tabName && id !== excludeId,
    )
  }

  /**
   * Add a new custom tab
   */
  addTab(tab: ITabNav) {
    this._tabs.push(tab)
  }

  /**
   * Remove a custom tab by id
   */
  removeTab(tabId: string) {
    this._tabs = this._tabs.filter((tab) => tab.id !== tabId)
  }

  /**
   * Update an existing tab
   */
  updateTab(tabId: string, updates: Partial<ITabNav>) {
    const index = this._tabs.findIndex((tab) => tab.id === tabId)
    if (index !== -1) {
      this._tabs[index] = { ...this._tabs[index], ...updates }
    }
  }

  /**
   * Reset tabs to default state (only default tabs)
   */
  resetToDefault() {
    this._tabs = [...this._defaultTabs]
  }
}

const tabNavConfig = new Config()

export { tabNavConfig }
